import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePos } from '@/store/pos'
import { api, ApiClientError } from '@/lib/api'
import { money, moneyPlain } from '@/lib/format'
import { toastError, toastSuccess } from '@/store/toast'
import CardScanModal from '@/components/CardScanModal'
import ManualCustomerModal from '@/components/ManualCustomerModal'
import CustomerSearchModal from '@/components/CustomerSearchModal'
import ProductCombobox from '@/components/ProductCombobox'
import type { Receipt, PaymentMethod, Customer } from '@yui-receipt/shared'

const PAYMENTS: { id: PaymentMethod; label: string; icon: string }[] = [
  { id: 'cash', label: 'เงินสด', icon: '💵' },
  { id: 'qr', label: 'QR', icon: '📱' },
  { id: 'card', label: 'บัตร', icon: '💳' },
  { id: 'transfer', label: 'โอน', icon: '🏦' },
]

export default function PosPage() {
  const navigate = useNavigate()
  const pos = usePos()
  const [scanOpen, setScanOpen] = useState(false)
  const [manualOpen, setManualOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  // When set, ManualCustomerModal opens in edit mode with this customer.
  const [editing, setEditing] = useState<Customer | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const subtotal = pos.subtotal()
  const subtotalVatable = pos.subtotalVatable()
  const subtotalExempt = pos.subtotalExempt()
  const vat = pos.vat()
  const total = pos.total()
  const hasExempt = subtotalExempt > 0

  const addItem = useCallback(() => pos.addItem(), [pos])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName
      const inInput = tag === 'INPUT' || tag === 'TEXTAREA'
      if (e.key === 'F2') {
        e.preventDefault()
        setScanOpen(true)
      } else if (e.key === 'F3') {
        e.preventDefault()
        setSearchOpen(true)
      } else if (e.key === 'F9') {
        e.preventDefault()
        void handleSubmit()
      } else if (e.key === 'Enter' && !inInput) {
        e.preventDefault()
        addItem()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos.items, pos.customer, pos.paymentMethod])

  async function handleSubmit() {
    if (pos.items.length === 0) {
      toastError('ยังไม่มีรายการ — เพิ่มอย่างน้อย 1 รายการ')
      return
    }
    if (pos.items.some((i) => !i.name.trim() || i.qty <= 0 || i.unitPrice < 0)) {
      toastError('กรุณากรอกชื่อรายการ + จำนวน + ราคาให้ถูกต้อง')
      return
    }
    setSubmitting(true)
    try {
      // Persist any line whose name doesn't match an existing product — so
      // next time staff see it as a suggestion. Non-fatal: a failure here
      // shouldn't block the receipt the customer is waiting on.
      try {
        const list = await api<{ products: { name: string }[] }>('/products?active=true')
        const known = new Set(list.products.map((p) => p.name.trim().toLowerCase()))
        const toCreate = pos.items
          .map((i) => ({ name: i.name.trim(), price: i.unitPrice }))
          .filter(
            (i) =>
              i.name &&
              i.price >= 0 &&
              !known.has(i.name.toLowerCase()),
          )
        // Dedup within the same receipt.
        const seen = new Set<string>()
        const unique = toCreate.filter((i) => {
          const k = i.name.toLowerCase()
          if (seen.has(k)) return false
          seen.add(k)
          return true
        })
        if (unique.length > 0) {
          await api('/products/bulk', {
            method: 'POST',
            body: { products: unique, mode: 'skip_existing' },
          })
        }
      } catch {
        // Silent — receipt still goes through.
      }

      const res = await api<{ receipt: Receipt }>('/receipts', {
        body: {
          // CustomerSearchModal emits a synthetic customer (id: 0) when the
          // selected patient has no linked billing customer. Posting 0 makes
          // the API chase a non-existent customer row and error with
          // "Customer not found" — normalize to null so the receipt is
          // issued without a customerId.
          customerId: pos.customer?.id ? pos.customer.id : null,
          // Snapshot the POS-side name/phone so the printed receipt shows the
          // patient's name instead of "Walk-in" when no billing customer is
          // on file. The backend ignores these when customerId is set.
          customerNameSnapshot: pos.customer?.nameTh ?? null,
          customerPhoneSnapshot: pos.customer?.phone ?? null,
          items: pos.items.map((i) => ({
            name: i.name.trim(),
            qty: i.qty,
            unitPrice: i.unitPrice,
            vatable: i.vatable,
            free: i.free,
          })),
          vatRate: pos.vatRate,
          discount: pos.discount,
          paymentMethod: pos.paymentMethod,
          receiptNo: pos.receiptNo.trim() || null,
          note: pos.note,
        },
      })
      toastSuccess(`ออกใบเสร็จ ${res.receipt.receiptNo ?? `#${res.receipt.id}`} สำเร็จ`)
      pos.reset()
      navigate(`/receipts/${res.receipt.id}`)
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'ออกใบเสร็จไม่สำเร็จ'
      toastError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  function onCustomer(c: Customer, _patient?: unknown) {
    pos.setCustomer(c)
    toastSuccess(`เพิ่มคนไข้ ${c.nameTh}`)
  }

  return (
    <div className="max-w-6xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Step 1 — Patient */}
      <section className="card lg:col-span-3">
        <h2 className="text-lg font-semibold mb-3">
          ① คนไข้
          <span className="ml-2 text-xs text-slate-500 font-normal">
            (F2 สแกนบัตร · F3 ค้นหา)
          </span>
        </h2>
        {!pos.customer ? (
          <div className="flex flex-wrap gap-2">
            <button className="btn-primary" onClick={() => setSearchOpen(true)}>
              🔍 ค้นหาคนไข้เดิม
            </button>
            <button className="btn-primary" onClick={() => setScanOpen(true)}>
              🪪 สแกนบัตร
            </button>
            <button className="btn-secondary" onClick={() => setManualOpen(true)}>
              ✍️ กรอกเอง
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-lg font-semibold">{pos.customer.nameTh}</div>
              <div className="text-sm text-slate-500 font-mono">
                {pos.customer.cidFull ?? pos.customer.cidMasked}
                {pos.customer.phone && (
                  <span className="ml-2">· 📞 {pos.customer.phone}</span>
                )}
              </div>
              {pos.customer.nameEn && (
                <div className="text-xs text-slate-400">{pos.customer.nameEn}</div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                className="btn-secondary"
                onClick={() => {
                  setEditing(pos.customer)
                  setManualOpen(true)
                }}
                title="แก้ไขข้อมูลคนไข้"
              >
                ✏️ แก้ไข
              </button>
              <button className="btn-secondary" onClick={() => setSearchOpen(true)}>
                🔍 เปลี่ยน
              </button>
              <button className="btn-ghost" onClick={() => pos.setCustomer(null)}>
                ล้าง
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Step 2 — Items */}
      <section className="card lg:col-span-2">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-lg font-semibold">② รายการ</h2>
          <button className="btn-primary" onClick={addItem}>
            + เพิ่มรายการ
          </button>
        </div>
        {pos.items.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">
            ยังไม่มีรายการ — กด "เพิ่มรายการ" หรือ Enter
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-left">
                  <th className="py-2 pl-2">รายการ</th>
                  <th className="py-2 w-20 text-right">จน.</th>
                  <th className="py-2 w-28 text-right">ราคา/หน่วย</th>
                  <th
                    className="py-2 w-14 text-center"
                    title="เลือกเมื่อราคารวม VAT แล้ว (ระบบจะแยก net + VAT ให้)"
                  >
                    VAT
                  </th>
                  <th className="py-2 w-14 text-center" title="แถมฟรี">ฟรี</th>
                  <th className="py-2 w-28 text-right">รวม</th>
                  <th className="py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {pos.items.map((it) => {
                  const row = it.free ? 0 : it.qty * it.unitPrice
                  return (
                    <tr key={it.key} className="border-t border-slate-100">
                      <td className="py-2 pl-2">
                        <ProductCombobox
                          value={it.name}
                          onChange={(name, price) =>
                            pos.updateItem(it.key, {
                              name,
                              ...(price != null ? { unitPrice: price } : {}),
                            })
                          }
                        />
                      </td>
                      <td className="py-2">
                        <input
                          className="input text-right"
                          type="number"
                          min={0}
                          step={1}
                          value={it.qty}
                          onChange={(e) =>
                            pos.updateItem(it.key, { qty: Number(e.target.value) || 0 })
                          }
                        />
                      </td>
                      <td className="py-2">
                        <input
                          className="input text-right"
                          type="number"
                          min={0}
                          step={0.01}
                          value={it.unitPrice}
                          onChange={(e) =>
                            pos.updateItem(it.key, {
                              unitPrice: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-brand-600 cursor-pointer"
                          checked={it.vatable}
                          onChange={(e) =>
                            pos.updateItem(it.key, { vatable: e.target.checked })
                          }
                          aria-label="มี VAT (ราคารวม VAT แล้ว)"
                          title={
                            it.vatable
                              ? 'มี VAT (ราคารวม VAT แล้ว)'
                              : 'ไม่มี VAT'
                          }
                        />
                      </td>
                      <td className="py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-emerald-600 cursor-pointer"
                          checked={it.free}
                          onChange={(e) =>
                            pos.updateItem(it.key, { free: e.target.checked })
                          }
                          aria-label="แถมฟรี"
                          title={it.free ? 'แถมฟรี (ไม่คิดเงิน)' : 'คิดเงิน'}
                        />
                      </td>
                      <td className="py-2 text-right font-medium">
                        {it.free ? (
                          <span className="text-emerald-600 text-xs">ฟรี</span>
                        ) : (
                          moneyPlain(row)
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          className="text-rose-600 hover:bg-rose-50 rounded-md p-2"
                          onClick={() => pos.removeItem(it.key)}
                          aria-label="ลบ"
                          title="ลบ"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Step 3 — Summary */}
      <section className="card lg:col-span-1">
        <h2 className="text-lg font-semibold mb-3">③ สรุป</h2>
        <dl className="space-y-2 text-base">
          {hasExempt && (
            <>
              <div className="flex justify-between text-sm">
                <dt className="text-slate-600">ยอดคิดภาษี</dt>
                <dd>{moneyPlain(subtotalVatable)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-slate-600">ยอดยกเว้นภาษี</dt>
                <dd>{moneyPlain(subtotalExempt)}</dd>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <dt className="text-slate-600">Subtotal</dt>
            <dd>{moneyPlain(subtotal)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-600">
              VAT {(pos.vatRate * 100).toFixed(0)}%
            </dt>
            <dd>{moneyPlain(vat)}</dd>
          </div>
          <div className="flex justify-between items-center gap-2 pt-2 border-t border-slate-200">
            <dt className="text-slate-600">ส่วนลด</dt>
            <dd className="flex items-center gap-1">
              <span className="text-slate-500">฿</span>
              <input
                className="input text-right w-28"
                type="number"
                min={0}
                step={0.01}
                value={pos.discount || ''}
                onChange={(e) => pos.setDiscount(Number(e.target.value) || 0)}
                placeholder="0.00"
                aria-label="ส่วนลด (บาท)"
              />
            </dd>
          </div>
          <div className="flex justify-between text-xl font-bold pt-2 border-t border-slate-200">
            <dt>รวม</dt>
            <dd>{money(total)}</dd>
          </div>
        </dl>

        <div className="mt-4">
          <div className="label">ช่องทางชำระ</div>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENTS.map((p) => (
              <button
                key={p.id}
                onClick={() => pos.setPaymentMethod(p.id)}
                className={
                  'btn ' +
                  (pos.paymentMethod === p.id
                    ? 'bg-brand-600 text-white'
                    : 'bg-slate-100 text-slate-800 hover:bg-slate-200')
                }
              >
                <span className="mr-1">{p.icon}</span> {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <label className="label">เลขที่เอกสาร (ถ้ามี)</label>
          <input
            className="input"
            value={pos.receiptNo}
            onChange={(e) => pos.setReceiptNo(e.target.value)}
            placeholder="กรอกเฉพาะกรณีต้องการพิมพ์เลขที่ลงใบเสร็จ"
          />
        </div>

        <div className="mt-4">
          <label className="label">หมายเหตุ</label>
          <textarea
            className="input min-h-[60px]"
            value={pos.note ?? ''}
            onChange={(e) => pos.setNote(e.target.value || null)}
            placeholder="บันทึกเพิ่มเติม (ถ้ามี)"
          />
        </div>

        <button
          className="btn-primary w-full mt-4 text-lg"
          onClick={handleSubmit}
          disabled={submitting || pos.items.length === 0}
        >
          {submitting ? 'กำลังบันทึก…' : '💾 ออกใบเสร็จ (F9)'}
        </button>
      </section>

      <CardScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onCustomer={onCustomer}
      />
      <ManualCustomerModal
        open={manualOpen}
        onClose={() => {
          setManualOpen(false)
          setEditing(null)
        }}
        onCustomer={onCustomer}
        customer={editing}
      />
      <CustomerSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={onCustomer}
        onEdit={(c) => {
          setEditing(c)
          setSearchOpen(false)
          setManualOpen(true)
        }}
      />
    </div>
  )
}
